/**
 * src/lib/api/vessel360.js -- TICKET-1331, pure helpers for the vessel page.
 *
 * "By boat, all the projects and the pipeline linked" (CEO 2026-10-19). Reading every
 * table by vessel_id alone misses real work: on Dama de Noche 15 of 18 bundles had
 * vessel_id NULL and the 3 signed PDFs were linked by deal_id only. The page therefore
 * reads by vessel_id, collects the deal and project ids it finds (linkedIds), reads the
 * rest by those ids, and merges (unionById). No DB writes; cloew-0b's ticket repairs the
 * links themselves.
 */

const add = (set, v) => { if (v !== null && v !== undefined && v !== '') set.add(String(v)); };

/** Every deal id and project id the boat's own rows point at. */
export function linkedIds({ deals = [], projects = [], quotes = [] } = {}) {
  const dealIds = new Set();
  const projectIds = new Set();
  for (const d of deals || []) { add(dealIds, d?.id); add(projectIds, d?.source_project_id); }
  for (const p of projects || []) { add(projectIds, p?.id); add(dealIds, p?.deal_id); }
  for (const q of quotes || []) { add(dealIds, q?.deal_id); add(projectIds, q?.project_id); }
  return { dealIds: [...dealIds], projectIds: [...projectIds] };
}

/** Merge lists by id, first copy wins, order kept. Rows without an id are dropped. */
export function unionById(...lists) {
  const seen = new Map();
  for (const list of lists) {
    for (const row of list || []) {
      if (row?.id === null || row?.id === undefined) continue;
      if (!seen.has(row.id)) seen.set(row.id, row);
    }
  }
  return [...seen.values()];
}

/**
 * The signed PDF of a quote: a document on the same deal named "Signed quote <n>.pdf"
 * (the name the signing flow writes). The number must match exactly -- 7 never 74.
 */
export function signedPdfForQuote(quote, documents = []) {
  if (!quote?.deal_id || quote.quote_number === null || quote.quote_number === undefined) return null;
  const re = new RegExp(`^signed quote\\s*#?${String(quote.quote_number)}(?!\\d)`, 'i');
  return (documents || []).find((d) => d?.deal_id === quote.deal_id && re.test(String(d.file_name || d.title || ''))) || null;
}

/** Bundles under their project; a bundle with only a deal goes under that deal. */
export function bundlesByProject(bundles = []) {
  const byProject = {};
  const byDeal = {};
  const unlinked = [];
  for (const b of bundles || []) {
    const pid = b?.project_fk || b?.parent_project_id;
    const did = b?.crm_deal_id || b?.deal_fk || b?.source_deal_id;
    if (pid) (byProject[pid] = byProject[pid] || []).push(b);
    else if (did) (byDeal[did] = byDeal[did] || []).push(b);
    else unlinked.push(b);
  }
  return { byProject, byDeal, unlinked };
}

/** invoices.status is sent / draft / paid in prod; a matched bank transaction is paid too. */
export function invoicePaid(inv) {
  return inv?.status === 'paid' || Boolean(inv?.matched_bank_transaction_id);
}
